/**
 * @module features/display-module/components/DdmStatusBadge
 * @description Semantic delivery status badges (`Live`, `Device Received`,
 * `Syncing v{version}`, `Paused`, `Expired`) and artifact type pills (`T031`).
 */

import * as React from "react";
import {
  CheckCircle2,
  Clock,
  RefreshCw,
  PauseCircle,
  History,
  Radio,
  Megaphone,
  Cake,
  AlertTriangle,
  Quote,
  Images,
  Sparkles,
  CalendarDays,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { DdmArtifactType, DdmDeliveryStatus } from "../types/ddm";
import { DDM_ARTIFACT_TYPE_META } from "../types/ddm";

interface DdmStatusBadgeProps {
  status: DdmDeliveryStatus;
  version: number;
  deviceReceived: boolean;
  isLive: boolean;
  className?: string;
}

export const DdmStatusBadge: React.FC<DdmStatusBadgeProps> = React.memo(
  ({ status, version, deviceReceived, className }) => {
    if (status === "LIVE") {
      return (
        <div className={cn("inline-flex flex-wrap items-center gap-1.5", className)}>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/12 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/25 px-2.5 py-0.5 text-[11px] font-semibold tracking-tight">
            <Radio className="h-3 w-3 text-emerald-500 animate-pulse" />
            <span>Live</span>
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 border border-border/70 dark:border-zinc-800 px-2 py-0.5 text-[10px] font-medium">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
            <span>Device Received</span>
            <span className="font-mono text-[10px] text-muted-foreground">
              v{version}
            </span>
          </span>
        </div>
      );
    }

    if (status === "DEVICE_RECEIVED_UPCOMING") {
      return (
        <div className={cn("inline-flex flex-wrap items-center gap-1.5", className)}>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/12 dark:bg-sky-500/20 text-sky-700 dark:text-sky-300 border border-sky-500/25 px-2.5 py-0.5 text-[11px] font-semibold">
            <Clock className="h-3 w-3" />
            <span>Upcoming</span>
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 px-2 py-0.5 text-[10px] font-medium">
            <CheckCircle2 className="h-3 w-3" />
            <span>Device Received v{version}</span>
          </span>
        </div>
      );
    }

    if (status === "UPDATE_PENDING_ACK") {
      return (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full bg-amber-500/12 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/25 px-2.5 py-0.5 text-[11px] font-semibold",
            className
          )}
        >
          <RefreshCw className="h-3 w-3 animate-spin" />
          <span>Syncing v{version}</span>
        </span>
      );
    }

    if (status === "PAUSED") {
      return (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full bg-orange-500/12 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 border border-orange-500/25 px-2.5 py-0.5 text-[11px] font-semibold",
            className
          )}
        >
          <PauseCircle className="h-3 w-3" />
          <span>Paused</span>
          {deviceReceived && (
            <span className="font-mono text-[10px] opacity-80">· Acked v{version}</span>
          )}
        </span>
      );
    }

    if (status === "EXPIRED") {
      return (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full bg-zinc-500/10 dark:bg-zinc-800/80 text-muted-foreground border border-border/70 dark:border-zinc-700/70 px-2.5 py-0.5 text-[11px] font-medium",
            className
          )}
        >
          <History className="h-3 w-3" />
          <span>Expired</span>
          <span className="font-mono text-[10px]">v{version}</span>
        </span>
      );
    }

    // Default: PENDING_DEVICE_ACK
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full bg-amber-500/12 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/25 px-2.5 py-0.5 text-[11px] font-semibold",
          className
        )}
      >
        <RefreshCw className="h-3 w-3 animate-spin" />
        <span>Syncing v{version}</span>
      </span>
    );
  }
);

DdmStatusBadge.displayName = "DdmStatusBadge";

const ARTIFACT_ICONS: Record<
  DdmArtifactType,
  React.ComponentType<{ className?: string }>
> = {
  notice: Megaphone,
  birthday: Cake,
  emergency_alert: AlertTriangle,
  quote: Quote,
  gallery: Images,
  custom_banner: Sparkles,
  event: CalendarDays,
};

const ARTIFACT_STYLES: Record<DdmArtifactType, string> = {
  notice:
    "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20",
  birthday:
    "bg-pink-500/10 text-pink-700 dark:text-pink-300 border-pink-500/20",
  emergency_alert:
    "bg-red-500/12 text-red-700 dark:text-red-300 border-red-500/30 font-semibold",
  quote:
    "bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20",
  gallery:
    "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
  custom_banner:
    "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/20",
  event:
    "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20",
};

interface DdmArtifactTypeBadgeProps {
  type: DdmArtifactType;
  className?: string;
}

export const DdmArtifactTypeBadge: React.FC<DdmArtifactTypeBadgeProps> =
  React.memo(({ type, className }) => {
    const Icon = ARTIFACT_ICONS[type] ?? Megaphone;
    const meta = DDM_ARTIFACT_TYPE_META[type];
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium",
          ARTIFACT_STYLES[type] ?? ARTIFACT_STYLES.notice,
          className
        )}
      >
        <Icon className="h-3 w-3 shrink-0" />
        <span>{meta?.label ?? type}</span>
      </span>
    );
  });

DdmArtifactTypeBadge.displayName = "DdmArtifactTypeBadge";

